import _ from 'lodash';
import { nanoid } from 'nanoid';
import { z } from 'zod';
import { StateCreator } from 'zustand';
import { createLens } from '@dhmk/zustand-lens';
import { DirtyEntries, DirtyType } from '@/types';
import {
  CreateEntryItem,
  CreateMapItem,
  TreeNode,
  isTreeGraphItem,
  isTreeItem,
  isTreeMap,
  isCodeOutputMap,
  createUnregisteredItem,
  createUnregisteredMap,
} from '@morph-mapper/types';

import { createMapChildrenAPI } from './children';
import type { IMapChildren } from './children';
import {
  createEntryAPI,
} from './entry';
import type {
  EntryReturnType,
  ICellEntry,
  IEntry,
  IGraphEntry,
  IInternalEntry,
  IMapEntry,
  ISimpleEntry,
  IBooleanEntry,
  ICodeEntry,
} from './entry';
import type { StoreSlice } from '@/store';
import { EntryItemType, EntryType } from '@morph-mapper/types';
import { StructureBlockIndex } from '@morph-mapper/node-logic';
import { createDependencyScanner, ScanMode } from '@/utils';
import type { Draft } from 'immer';
import { match } from 'ts-pattern';
import {
  type GraphId,
  GraphRefType,
} from '../../components/graph-editor/types';
import { ROOT_KEY } from '@/config';

export type {
  IEntry,
  ICodeEntry,
  IBooleanEntry,
  ISimpleEntry,
  ICellEntry,
  IGraphEntry,
  IInternalEntry,
  IMapEntry,
  IMapChildren,
};

export interface EntrySlice {
  rootId: string | undefined;
  entries: Record<string, TreeNode>;
  dirtyEntries: DirtyEntries;

  // Helpers
  children: (id: string) => IMapChildren;
  entry: <T extends EntryType>(id: string, type?: T) => EntryReturnType<T>;
  // TODO: maybe we want to make all usage in the application of type IEntry
  // instead of having access to the entry object directly
  getParent: (id: string) => IMapEntry | undefined;

  // Operators on a single entry
  createItem: (
    partial: CreateEntryItem,
    type: EntryItemType,
    prefill?: string,
  ) => string;
  createMap: (partial: CreateMapItem) => string;
  getById: (id: string) => TreeNode;
  getByKey: (name: string) => IEntry[];
  getRootId: () => string;
  clear: (id: string) => void;

  addDirty: (id: string, type: DirtyType) => void;
  addDirtyAll: (type: DirtyType) => void;
  getAllDirty: (type: DirtyType) => string[];
  clearDirty: (type: DirtyType) => void;
  removeDirtyBatch: (ids: string[], type: DirtyType) => void;

  linkChild: (parentId: string, childId: string) => void;
  linkGraph: (entryId: string, graphId: GraphId) => void;
  getParentId: (id: string) => string | undefined;
  getAllowedTypes: (id: string) => EntryItemType[];
  getType: (id: string) => EntryType;
  setType: (id: string, type: EntryItemType) => void;
  getKey: (id: string) => string;
  getName: (id: string) => string | undefined;
  getComputed: (id: string) => any;
  setComputed: (id: string, computed: any) => void;
  getValidation: (id: string) => z.ZodType<any, any, any> | undefined;
  getOutputType: (id: string) => StructureBlockIndex;
  setOutputType: (id: string, outputType: StructureBlockIndex) => void;
  getFormerOutputType: (id: string) => StructureBlockIndex;
  setFormerOutputType: (id: string, outputType: StructureBlockIndex) => void;
  getDependsOn: (id: string) => string[];
  getRequiredBy: (id: string) => string[];
  getPath: (id: string) => string[];
  getUpdateEnabled: (id: string) => boolean;
  setUpdateEnabled: (id: string, enabled: boolean) => void;

  getUnderlyingValue: (id: string) => any;
  setUnderlyingValue: (id: string, value: any) => void;

  getTemplateValue: (id: string) => any;
  setTemplateValue: (id: string, value: any) => void;

  hasValue: (id: string) => boolean;
  copy: (sourceId: string, targetId: string) => void;
  copyEntryToClipboard: (id: string) => Promise<void>;
  pasteEntryFromClipboard: (targetId: string) => Promise<boolean>;
  delete: (id: string) => void;

  // Operators on multiple entries
  hasEntries: () => boolean;
  setEntries: (entries: Record<string, TreeNode>) => void;
  getEntries: () => Record<string, TreeNode>;
}

export const createEntrySlice: StateCreator<
  StoreSlice,
  [['zustand/immer', never]],
  [],
  EntrySlice
> = (_set, _get) => {
  // Slices make use of a namespace, make current slice local namespace.
  const [set, get] = createLens(_set, _get, ['entries']);

  const _getEntrySafe = <T extends EntryType>(id: string, type?: T) => {
    const entry = get().entries[id];
    if (!entry) {
      throw `[EntrySlice]: Entry ${id} does not exist`;
    }
    if (!type) {
      return entry;
    }
    if (entry.type !== type) {
      throw `[EntrySlice]: Entry ${id} is of type ${entry.type}, expected ${type}`;
    }

    return entry;
  };

  const _setEntrySafe = <T extends EntryType>(
    s: Draft<EntrySlice>,
    id: string,
    type?: T,
  ) => {
    const entry = s.entries[id];
    if (!entry) {
      throw `[EntrySlice]: Entry ${id} does not exist`;
    }
    if (!type) {
      return entry;
    }
    if (entry.type !== type) {
      throw `[EntrySlice]: Entry ${id} is of type ${entry.type}, expected ${type}`;
    }

    return entry;
  };

  return {
    rootId: undefined,
    entries: {},
    dirtyEntries: {
      [DirtyType.Underlying]: [],
      [DirtyType.Template]: [],
    },

    children: (id) => createMapChildrenAPI(set, get, id),
    entry: (id, type) => createEntryAPI(set, get, id, type),

    getParent: (id) => {
      const entry = _getEntrySafe(id);
      const parentId = entry.parentId;
      if (!parentId) {
        return undefined;
      }

      return get().entry(parentId, EntryType.Map);
    },

    addDirty: (id, type) => {
      set((s) => {
        if (s.entries[id].type === EntryType.Map) return;
        s.dirtyEntries[type].push(id);
      });
    },
    // TODO: Temporary brute-force fallback. Reduce reliance on this by
    // using dependency-driven invalidation so only affected entries are dirty.
    addDirtyAll: (type) => {
      set((s) => {
        const { scanDependencies } = createDependencyScanner(s.entries);
        const dependencies = scanDependencies(ScanMode.All);
        dependencies.forEach((entryDependencies, id) => {
          if (s.entries[id] !== undefined) {
            s.entries[id].dependencies = entryDependencies;
          }
        });

        const existing = new Set(
          s.dirtyEntries[type].filter((id) => s.entries[id] !== undefined),
        );
        Object.values(s.entries).forEach((entry) => {
          if (entry.type === EntryType.Map) return;
          const hasUnderlying = match(entry)
            .with({ type: EntryType.Cell }, (e) => e.column !== undefined)
            .with({ type: EntryType.Graph }, (e) => e.graphId !== undefined)
            .otherwise((e) => e.value !== undefined);
          if (!hasUnderlying) return;
          existing.add(entry.id);
        });
        s.dirtyEntries[type] = [...existing];
      });
    },
    getAllDirty: (type) => {
      const dirty = get().dirtyEntries[type];

      return dirty;
    },
    clearDirty: (type) => {
      set((s) => {
        s.dirtyEntries[type] = [];
      });
    },
    removeDirtyBatch: (ids, type) => {
      set((s) => {
        const idSet = new Set(ids);
        s.dirtyEntries[type] = s.dirtyEntries[type].filter(
          (id) => !idSet.has(id),
        );
      });
    },
    createItem: (partial, type, prefill) => {
      let id = nanoid(10);

      set((s) => {
        while (s.entries[id]) {
          id = nanoid(10);
        }

        const entry = {
          id,
          key: partial.key,
          name: partial.name ?? partial.key,
          value: prefill,
          validation: partial.validation ?? z.any(),
          parentId: partial.parentId,
          allowedTypes: partial.allowedTypes,
          semantics: partial.semantics,
        };

        s.entries[id] = { id, ...createUnregisteredItem(type, entry) };
      });

      return id;
    },
    createMap: (partial) => {
      let id = nanoid(10);

      set((s) => {
        while (s.entries[id]) {
          id = nanoid(10);
        }

        s.entries[id] = {
          id,
          ...createUnregisteredMap(partial),
        };
      });

      return id;
    },
    getById: (id) => {
      return _getEntrySafe(id);
    },
    getByKey: (name) => {
      const entries = Object.values(get().entries).filter(
        (entry) => entry.key === name,
      );
      if (entries.length === 0) {
        throw new Error(`[EntrySlice]: Entry ${name} does not exist`);
      }
      return entries.map((entry) => get().entry(entry.id));
    },
    getRootId: () => {
      //TODO: optimize
      const rootId = get().rootId;
      if (rootId) return rootId;

      const root = Object.values(get().entries).find(
        (entry) => entry.name === ROOT_KEY,
      );
      if (!root) {
        throw new Error('[EntrySlice]: Root entry not found');
      }

      return root.id;
    },
    linkChild: (parentId, childId) => {
      const parent = _getEntrySafe(parentId);
      if (!isTreeMap(parent)) {
        throw new Error('[EntrySlice]: Invalid parent entry');
      }
      const child = _getEntrySafe(childId);

      get().children(parentId).set(childId, child.key);
    },
    linkGraph: (entryId, graphId) => {
      set((s) => {
        const entry = _setEntrySafe(s, entryId);
        if (!isTreeGraphItem(entry)) {
          throw new Error('[EntrySlice]: Invalid entry type');
        }

        if (entry.graphId !== undefined) {
          throw new Error(
            `[EntrySlice]: Cannot overwrite existing graph with id ${entry.graphId}.`,
          );
        }

        entry.graphId = graphId;
      });
    },
    getParentId: (id) => {
      return _getEntrySafe(id).parentId;
    },
    getAllowedTypes: (id) => {
      return _getEntrySafe(id).allowedTypes ?? [];
    },
    getType: (id) => {
      return _getEntrySafe(id).type;
    },
    setType: (id, type) => {
      set((s) => {
        const entry = _getEntrySafe(id);
        const { name, parentId, validation, key, allowedTypes } = entry;
        if (!parentId || !validation) {
          throw new Error('[EntrySlice]: Cannot change entry type');
        }

        const partial = { name, parentId, validation, key, allowedTypes };

        s.entries[id] = { id, ...createUnregisteredItem(type, partial) };
      });
    },
    getKey: (id) => {
      return _getEntrySafe(id).key;
    },
    getName: (id) => {
      // TODO: should be made safe
      const entry = get().entries[id];
      if (!entry) {
        return undefined;
      }

      return entry.name;
    },
    getComputed: (id) => {
      const entry = _getEntrySafe(id);

      if (isCodeOutputMap(entry)) {
        return entry.computed;
      }

      if (!isTreeItem(entry))
        throw new Error('[EntrySlice]: Invalid entry type');

      return entry.computed;
    },
    setComputed: (id, computed) => {
      set((s) => {
        const entry = _setEntrySafe(s, id);
        if (isCodeOutputMap(entry)) {
          entry.computed = computed;
          return;
        }

        if (!isTreeItem(entry))
          throw new Error('[EntrySlice]: Invalid entry type');

        entry.computed = computed;
      });
    },
    getValidation: (id) => {
      const entry = _getEntrySafe(id);
      if (!isTreeItem(entry))
        throw new Error('[EntrySlice]: Invalid entry type');

      return entry.validation;
    },
    getOutputType: (id) => {
      const entry = _getEntrySafe(id);
      if (!isTreeMap(entry))
        throw new Error('[EntrySlice]: Invalid entry type (output)');

      return entry.outputType;
    },
    setOutputType: (id, outputType) => {
      set((s) => {
        const entry = _setEntrySafe(s, id);
        if (!isTreeMap(entry))
          throw new Error('[EntrySlice]: Invalid entry type');

        entry.outputType = outputType;
      });
    },
    getFormerOutputType: (id) => {
      const entry = _getEntrySafe(id);
      if (!isTreeMap(entry))
        throw new Error('[EntrySlice]: Invalid entry type (output)');

      return entry.formerOutputType;
    },
    setFormerOutputType: (id, outputType) => {
      set((s) => {
        const entry = _setEntrySafe(s, id);
        if (!isTreeMap(entry))
          throw new Error('[EntrySlice]: Invalid entry type');

        entry.formerOutputType = outputType;
      });
    },
    getDependsOn: (id) => {
      const entry = _getEntrySafe(id);
      return Array.from(entry.dependencies.dependsOn);
    },
    getRequiredBy: (id) => {
      const entry = _getEntrySafe(id);
      return Array.from(entry.dependencies.requiredBy);
    },
    getPath: (id) => {
      const entry = _getEntrySafe(id);
      const path = [entry.id];
      let parentId = entry.parentId;

      while (parentId !== undefined) {
        path.push(parentId);

        const parent = _getEntrySafe(parentId);
        parentId = parent.parentId;
      }

      return path.reverse();
    },
    getUpdateEnabled: (id) => {
      const entry = _getEntrySafe(id);
      return entry.updateObject?.enabled ?? false;
    },
    setUpdateEnabled: (id, enabled) => {
      set((s) => {
        const entry = _setEntrySafe(s, id);
        entry.updateObject = { ...entry.updateObject, enabled };
      });
    },
    setUnderlyingValue: (id, value) => {
      set((s) => {
        const entry = _setEntrySafe(s, id);

        if (isCodeOutputMap(entry)) {
          entry.value = value;
          s.dirtyEntries[DirtyType.Underlying].push(id);
          return;
        }

        if (!isTreeItem(entry))
          throw new Error('[EntrySlice]: Invalid entry type');

        // For simple and internal entries the value is the same as the underlying value
        // simply set them anyway here so the listener for template values is triggered.
        match(entry)
          .with({ type: EntryType.Boolean }, (e) => {
            e.value = value;
          })
          .with({ type: EntryType.Simple }, (e) => {
            e.value = value;
          })
          .with({ type: EntryType.Cell }, (e) => {
            e.column = value;
          })
          .with({ type: EntryType.Graph }, (e) => {
            e.graphId = value;
          })
          .with({ type: EntryType.Code }, (e) => {
            e.value = value;
          })
          .with({ type: EntryType.Internal }, (e) => {
            e.value = value;
          })
          .exhaustive();

        s.dirtyEntries[DirtyType.Underlying].push(id);
      });
    },
    getUnderlyingValue: (id) => {
      const entry = _getEntrySafe(id);
      if (isCodeOutputMap(entry)) {
        return entry.value;
      }

      if (!isTreeItem(entry))
        throw new Error('[EntrySlice]: Invalid entry type');

      return match(entry)
        .with({ type: EntryType.Boolean }, (e) => e.value)
        .with({ type: EntryType.Simple }, (e) => e.value)
        .with({ type: EntryType.Cell }, (e) => e.column)
        .with({ type: EntryType.Graph }, (e) => e.graphId)
        .with({ type: EntryType.Code }, (e) => e.value)
        .with({ type: EntryType.Internal }, (e) => e.value)
        .exhaustive();
    },
    getTemplateValue: (id) => {
      const entry = _getEntrySafe(id);
      if (isCodeOutputMap(entry)) {
        return entry.value;
      }
      if (!isTreeItem(entry))
        throw new Error('[EntrySlice]: Invalid entry type');
      return entry.value;
    },
    setTemplateValue: (id, value) => {
      set((s) => {
        const entry = _setEntrySafe(s, id);
        if (isTreeMap(entry)) {
          if (entry.outputType !== 'code') {
            throw new Error('[EntrySlice]: Invalid entry type');
          }
        }

        entry.value = value;

        const { scanDependencies } = createDependencyScanner(s.entries);
        // TODO: We might be throwing away important dependency information here,
        // check if this results in any inconsistencies.
        let dependencies = undefined;
        // TODO: We throw away the dependencies for code entries here,
        // this is to prevent erros from user input in scanDependencies. This should be made more robust instead.
        // Maybe by adding an error list to the entry?
        // if (entry.type === EntryType.Code) {
        //   dependencies = {
        //     dependsOn: new Set<string>(),
        //     requiredBy: new Set<string>(),
        //   };
        // } else {
        //
        // }
        dependencies = scanDependencies(ScanMode.Singular, id).get(id);

        // Update dependencies
        if (!dependencies) {
          throw new Error('[EntrySlice]: Dependencies not found');
        }
        entry.dependencies = dependencies;

        if (value === undefined) {
          entry.computed = undefined;
          return;
        }
        if (!s.dirtyEntries[DirtyType.Template].includes(id)) {
          s.dirtyEntries[DirtyType.Template].push(id);
        }
      });
      const entry = _getEntrySafe(id);
      if (isTreeMap(entry)) {
        if (entry.outputType !== 'code') {
          throw new Error('[EntrySlice]: Invalid entry type');
        }
      }
    },
    hasValue: (id) => {
      const entry = _getEntrySafe(id);
      if (!isTreeItem(entry))
        throw new Error('[EntrySlice]: Invalid entry type');

      return match(entry)
        .with({ type: EntryType.Boolean }, (e) => e.value !== undefined)
        .with({ type: EntryType.Simple }, (e) => e.value !== undefined)
        .with({ type: EntryType.Cell }, (e) => e.column !== undefined)
        .with({ type: EntryType.Graph }, (e) => e.graphId !== undefined)
        .with({ type: EntryType.Code }, (e) => e.value !== undefined)
        .with({ type: EntryType.Internal }, (e) => e.value !== undefined)
        .exhaustive();
    },
    copy: (sourceId, targetId) => {
      if (sourceId === targetId) return;

      const entry = _getEntrySafe(sourceId);
      if (!isTreeItem(entry))
        throw new Error('[EntrySlice]: Invalid entry type');
      const target = _getEntrySafe(targetId);
      if (!isTreeItem(target))
        throw new Error('[EntrySlice]: Invalid entry type');

      set((s) => {
        const targetEntry = _setEntrySafe(s, targetId);
        if (!isTreeItem(targetEntry))
          throw new Error('[EntrySlice]: Invalid entry type');

        // We make the target entry the same type as the source entry,
        // otherwise the copied value might be misinterpreted.
        targetEntry.type = entry.type;

        // Copy over the dependencies, this immediately clears the old dependencies and
        // saves a recompute of the dependencies.
        targetEntry.dependencies = _.cloneDeep(entry.dependencies);
      });

      // If the target was a graph, delete the old graph before copying the new one.
      const previousTargetGraphId =
        target.type === EntryType.Graph ? target.graphId : undefined;
      if (previousTargetGraphId) {
        _get().graphs.deleteGraph(previousTargetGraphId);
      }

      // Copy over the value, go over all types as they define the value differently.
      match(entry)
        .with({ type: EntryType.Boolean }, (e) => {
          get().setUnderlyingValue(targetId, e.value);
        })
        .with({ type: EntryType.Simple }, (e) => {
          get().setUnderlyingValue(targetId, e.value);
        })
        .with({ type: EntryType.Cell }, (e) => {
          get().setUnderlyingValue(targetId, e.column);
        })
        .with({ type: EntryType.Code }, (e) => {
          get().setUnderlyingValue(targetId, e.value);
        })
        .with({ type: EntryType.Graph }, (e) => {
          // If there is no graphId, we don't need to copy anything
          if (e.graphId === undefined) return;

          const newGraphId = _get().graphs.copyTree(e.graphId, {
            kind: GraphRefType.Field,
            fieldId: targetId,
          });

          get().setUnderlyingValue(targetId, newGraphId);
        })
        .with({ type: EntryType.Internal }, (e) => {
          get().setUnderlyingValue(targetId, e.value);
        })
        .exhaustive();
    },

    copyEntryToClipboard: async (id) => {
      const entry = _getEntrySafe(id);
      if (!isTreeItem(entry)) return;

      type SGraph = { nodes: Record<string, any>; edges: Record<string, any>; children: Array<{ nodeId: string; optionKey: string; tree: SGraph }> };

      const serializeGraph = (graphId: string): SGraph => {
        const graph = _get().graphs.get(graphId);
        const nodes = _.cloneDeep(graph.nodes) as Record<string, any>;
        const edges = _.cloneDeep(graph.edges) as Record<string, any>;
        const children: SGraph['children'] = [];
        for (const [nodeId, node] of Object.entries(nodes)) {
          for (const [optionKey, option] of Object.entries((node as any).options ?? {})) {
            const opt = option as any;
            if (opt?.type === 'graph' && typeof opt.graphId === 'string') {
              children.push({ nodeId, optionKey, tree: serializeGraph(opt.graphId) });
              opt.graphId = `__child:${children.length - 1}`;
            }
          }
        }
        return { nodes, edges, children };
      };

      const body = match(entry)
        .with({ type: EntryType.Simple }, (e) => ({ entryType: 'Simple', value: e.value }))
        .with({ type: EntryType.Boolean }, (e) => ({ entryType: 'Boolean', value: e.value }))
        .with({ type: EntryType.Cell }, (e) => ({ entryType: 'Cell', column: e.column }))
        .with({ type: EntryType.Code }, (e) => ({ entryType: 'Code', value: e.value }))
        .with({ type: EntryType.Internal }, (e) => ({ entryType: 'Internal', value: e.value }))
        .with({ type: EntryType.Graph }, (e) => e.graphId ? { entryType: 'Graph', graphTree: serializeGraph(e.graphId) } : null)
        .exhaustive();

      if (!body) return;
      try {
        await navigator.clipboard.writeText(JSON.stringify({ _morph: 'v1', ...body }));
      } catch {
        throw new Error('size-exceeded');
      }
    },

    pasteEntryFromClipboard: async (targetId) => {
      let raw: string;
      try { raw = await navigator.clipboard.readText(); }
      catch { throw new Error('permission-denied'); }

      let data: any;
      try { data = JSON.parse(raw); } catch { return false; }
      if (data?._morph !== 'v1') return false;

      const target = _getEntrySafe(targetId);
      if (!isTreeItem(target)) return false;

      type SGraph = { nodes: Record<string, any>; edges: Record<string, any>; children: Array<{ nodeId: string; optionKey: string; tree: SGraph }> };

      const deserializeGraph = (tree: SGraph, ref: { kind: string; fieldId: string; graphId?: string; ownerNodeId?: string; paramKey?: string }): string => {
        const newGraphId = nanoid(10);
        const nodes = _.cloneDeep(tree.nodes) as Record<string, any>;
        const edges = _.cloneDeep(tree.edges) as Record<string, any>;
        for (const child of tree.children) {
          const childRef = { kind: GraphRefType.Param, graphId: newGraphId, fieldId: ref.fieldId, ownerNodeId: child.nodeId, paramKey: child.optionKey };
          const childGraphId = deserializeGraph(child.tree, childRef);
          const opt = (nodes[child.nodeId] as any)?.options?.[child.optionKey];
          if (opt) opt.graphId = childGraphId;
        }
        _get().graphs.init({ id: newGraphId, ref: ref as any, nodes, edges });
        if (ref.kind === GraphRefType.Param && ref.graphId) _get().graphs.registerGraph(ref.graphId, newGraphId);
        return newGraphId;
      };

      let newGraphId: string | undefined;
      let newType: EntryType | undefined;
      let newValue: any;

      if (data.entryType === 'Graph' && data.graphTree) {
        newGraphId = deserializeGraph(data.graphTree, { kind: GraphRefType.Field, fieldId: targetId });
        newType = EntryType.Graph;
      } else {
        const typeMap: Record<string, EntryType> = { Simple: EntryType.Simple, Boolean: EntryType.Boolean, Cell: EntryType.Cell, Code: EntryType.Code, Internal: EntryType.Internal };
        newType = typeMap[data.entryType];
        if (!newType) return false;
        newValue = data.entryType === 'Cell' ? data.column : data.value;
      }

      const oldGraphId = target.type === EntryType.Graph ? (target as any).graphId : undefined;
      if (oldGraphId) _get().graphs.deleteGraph(oldGraphId);

      set((s) => {
        const e = s.entries[targetId] as any;
        e.type = newType;
        e.value = newType !== EntryType.Graph && newType !== EntryType.Cell ? newValue : undefined;
        e.column = newType === EntryType.Cell ? newValue : undefined;
        e.graphId = newType === EntryType.Graph ? newGraphId : undefined;
        e.computed = undefined;
        e.dependencies = { dependsOn: new Set(), requiredBy: new Set() };
        s.dirtyEntries[DirtyType.Underlying].push(targetId);
      });

      return true;
    },

    clear: (id) => {
      set((s) => {
        const entry = _setEntrySafe(s, id);
        if (!isTreeItem(entry))
          throw new Error('[EntrySlice]: Invalid entry type');

        entry.computed = undefined;
        entry.value = undefined;

        match(entry)
          .with({ type: EntryType.Cell }, (e) => {
            e.column = undefined;
          })
          .with({ type: EntryType.Graph }, (e) => {
            if (e.graphId) _get().graphs.deleteGraph(e.graphId);
            e.graphId = undefined;
          })
          .otherwise(() => null);
      });
    },
    delete: (id) => {
      const entry = _getEntrySafe(id);
      // Recursively delete children
      if (entry.type === EntryType.Map) {
        get()
          .children(entry.id)
          .getIds()
          .forEach((childId) => {
            get().delete(childId);
          });
      }

      // Unlink from parent
      if (entry.parentId) {
        get().children(entry.parentId).deleteById(entry.id);
      }

      // Unlink from graph
      if (entry.type === EntryType.Graph && entry.graphId) {
        _get().graphs.deleteGraph(entry.graphId);
      }

      set((s) => {
        delete s.entries[id];
      });
    },

    hasEntries: () => {
      return Object.keys(get().entries).length > 0;
    },
    setEntries: (entries) => {
      set((s) => {
        s.entries = entries;
      });
    },
    getEntries: () => {
      return get().entries;
    },
  };
};
